import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { usePncpPurchaseContractDetailController } from './context/pncp-purchase-contract-detail-controller';
import { usePncpPurchaseContractDetailState } from './context/pncp-purchase-contract-detail-context';
import { formatDate, formatCurrency, formatDocument, openExternalLink } from 'utils';

function InfoField({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
      <Box sx={{ mr: 1, mt: 0.5, color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

export function PncpPurchaseContractDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail } = usePncpPurchaseContractDetailController();
  const { contract: detail, loading, error } = usePncpPurchaseContractDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const backButton = (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 3 }}>
      <Button
        variant="contained"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/contratos-aquisicoes')}
        size="large"
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4, py: 1.5 }}
      >
        Voltar para Contratos e Aquisições
      </Button>
    </Box>
  );

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={3} />;
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
          {backButton}
        </Container>
      </PageContainer>
    );
  }

  if (!detail?.contract) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 2 }}>
            Contrato não encontrado.
          </Alert>
          {backButton}
        </Container>
      </PageContainer>
    );
  }

  const contract = detail.contract;
  const number = contract.contractEffortNumber || `${contract.sequence}/${contract.yearContract}`;
  const documents = detail.documents ?? [];
  const terms = detail.terms ?? [];

  const values = [
    { label: 'Valor Inicial', value: contract.initialValue, color: 'text.primary' },
    { label: 'Valor Global', value: contract.globalValue, color: 'success.main' },
    { label: 'Valor da Parcela', value: contract.parcelValue, color: 'text.primary' },
    { label: 'Valor Acumulado', value: contract.accumulatedValue, color: 'warning.main' },
  ].filter((item) => item.value != null);

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={`Contrato ${number}`}
          subtitle={contract.supplierName || 'Detalhes do contrato'}
          breadcrumbs={[
            { label: 'Contratos e Aquisições', href: '/contratos-aquisicoes' },
            { label: number },
          ]}
        />

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ArticleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {number}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip icon={<VerifiedIcon />} label="PNCP" color="success" />
                  {contract.situationContract && <Chip label={contract.situationContract} variant="outlined" color="primary" />}
                </Box>
              </Box>
            </Box>
            {detail.externalUrl && (
              <Button
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                onClick={() => openExternalLink(detail.externalUrl)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Ver no PNCP
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            {contract.object && (
              <Grid item xs={12}>
                <InfoField icon={<DescriptionIcon />} label="Objeto do Contrato">
                  <Typography variant="body1">{contract.object}</Typography>
                </InfoField>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <InfoField icon={<BusinessIcon />} label="Empresa Contratada">
                <Typography variant="body1" fontWeight={600}>
                  {contract.supplierName || 'Não informado'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  CNPJ/CPF: {formatDocument(contract.supplierNumberIdentification)}
                </Typography>
              </InfoField>
            </Grid>

            {contract.processNumber && (
              <Grid item xs={12} md={6}>
                <InfoField icon={<DescriptionIcon />} label="Número do Processo">
                  <Typography variant="body1">{contract.processNumber}</Typography>
                </InfoField>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <InfoField icon={<CalendarTodayIcon />} label="Vigência">
                <Typography variant="body1" fontWeight={600}>
                  {formatDate(contract.effectiveDateStart)} a {formatDate(contract.effectiveDateEnd)}
                </Typography>
                {contract.signDate && (
                  <Typography variant="caption" color="text.secondary">
                    Assinado em {formatDate(contract.signDate)}
                  </Typography>
                )}
              </InfoField>
            </Grid>

            {contract.numeroControlePNCP && (
              <Grid item xs={12} md={6}>
                <InfoField icon={<VerifiedIcon />} label="Número de Controle PNCP">
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>{contract.numeroControlePNCP}</Typography>
                </InfoField>
              </Grid>
            )}

            {contract.notes && (
              <Grid item xs={12}>
                <InfoField icon={<DescriptionIcon />} label="Informações Complementares">
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{contract.notes}</Typography>
                </InfoField>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Valores */}
        {values.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AttachMoneyIcon sx={{ mr: 1 }} />
              Valores
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {values.map((item) => (
                <Grid item xs={12} sm={6} md={12 / values.length} key={item.label}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {item.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color={item.color}>
                        {formatCurrency(item.value)}
                      </Typography>
                      {item.label === 'Valor da Parcela' && contract.numberOfInstallments != null && (
                        <Typography variant="caption" color="text.secondary">
                          {contract.numberOfInstallments} parcela(s)
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Termos aditivos */}
        {terms.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryEduIcon sx={{ mr: 1 }} />
              Termos do Contrato ({terms.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {terms.map((term) => (
                <Grid item xs={12} key={term.sequentialContractTerm}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {term.contractTermType || 'Termo'} {term.contractTermNumber || term.sequentialContractTerm}
                        </Typography>
                        {term.signDate && <Chip label={`Assinado em ${formatDate(term.signDate)}`} size="small" variant="outlined" />}
                      </Box>
                      {term.object && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {term.object}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Vigência: {formatDate(term.effectiveDateStart)} a {formatDate(term.effectiveDateEnd)}
                      </Typography>
                      {(term.increaseValue != null || term.finalValue != null) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {term.increaseValue != null && `Acréscimo: ${formatCurrency(term.increaseValue)}`}
                          {term.increaseValue != null && term.finalValue != null && ' · '}
                          {term.finalValue != null && `Valor final: ${formatCurrency(term.finalValue)}`}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Documentos */}
        {documents.length > 0 ? (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionIcon sx={{ mr: 1 }} />
              Documentos ({documents.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List>
              {documents.map((doc) => (
                <ListItem
                  key={doc.number}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  secondaryAction={
                    doc.fileUrl && (
                      <Tooltip title="Abrir documento no PNCP" arrow>
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => openExternalLink(doc.fileUrl)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                            },
                          }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemIcon>
                    <DescriptionIcon color="error" sx={{ fontSize: 36 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.title || doc.documentTypeDescription || 'Documento'}
                    secondary={
                      <Typography variant="caption" display="block" component="span">
                        {doc.documentTypeDescription}
                        {doc.publishDate && ` · Publicado em ${formatDate(doc.publishDate)}`}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum documento disponível para este contrato.
          </Alert>
        )}

        {backButton}
      </Container>
    </PageContainer>
  );
}
